/**
 * Confidence Assessment
 * Computes confidence level based on data completeness and quality
 */

import type { SystemRecInput, ConfidenceSummary } from './types';

/**
 * Compute confidence summary from input data
 * High: annual consumption (kWh) + heat loss present + system layout known
 * Medium: any 1 anchor present, or multiple "unknown" fields resolved
 * Low: neither anchor present and key physical constraints unknown
 */
export function computeConfidenceSummary(input: SystemRecInput): ConfidenceSummary {
  const reasons: string[] = [];
  const toImprove: ConfidenceSummary['toImprove'] = [];

  // Check for anchor data points
  const hasAnnualConsumption = !!input.baselineEnabled && 
    (input.currentSystemEfficiency?.sedbukSeasonal != null || 
     input.currentSystemEfficiency?.value != null);
  
  const hasHeatLoss = !!(input.heatLossKw || input.knownHeatLossKw);
  
  const hasSystemLayout = !!(
    input.emitters?.type && input.emitters.type !== 'unknown' &&
    input.emitters?.pipeBore && input.emitters.pipeBore !== 'unknown'
  );

  const hasPipeworkInfo = !!(
    input.pipeworkInScreed && input.pipeworkInScreed !== 'unknown' &&
    input.systemAlreadySealed && input.systemAlreadySealed !== 'unknown'
  );

  // Count anchors
  const anchorCount = [hasAnnualConsumption, hasHeatLoss, hasSystemLayout].filter(Boolean).length;

  // Determine confidence level
  let level: "low" | "medium" | "high";
  
  if (hasAnnualConsumption && hasHeatLoss && hasSystemLayout) {
    level = "high";
  } else if (anchorCount >= 1) {
    level = "medium";
  } else {
    level = "low";
  }

  // Generate reasons (max 3, most relevant)
  if (!hasAnnualConsumption) {
    reasons.push("Annual energy consumption not provided");
    toImprove.push({
      key: "annual_kwh",
      label: "Annual energy usage",
      action: "Upload or enter annual kWh from energy bill"
    });
  }

  if (!hasHeatLoss) {
    reasons.push("Heat loss estimated from typical assumptions");
    toImprove.push({
      key: "heat_loss",
      label: "Whole-house heat loss",
      action: "Provide whole-house heat loss (kW) or room-by-room later"
    });
  }

  if (!hasSystemLayout) {
    reasons.push("Pipework layout/size unknown");
    toImprove.push({
      key: "pipework_layout",
      label: "Pipework configuration",
      action: "Confirm primary pipe size (22/28) and layout (Y/S)"
    });
  }

  if (!hasPipeworkInfo && toImprove.length < 3) {
    reasons.push("Hot water demand profile incomplete");
    toImprove.push({
      key: "pipework_assessment",
      label: "Pipework and sealing status",
      action: "Confirm if pipework is in screed and whether system is already sealed"
    });
  }

  // Limit to max 3
  return {
    level,
    reasons: reasons.slice(0, 3),
    toImprove: toImprove.slice(0, 3)
  };
}
